import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { TimelineEvent } from "@/domain/troubleshooting.interface";
import { formatInBackofficeLondon } from "@/utils/dateUtils";
import { OutcomeChip } from "./outcome";
import { EventTypeTag } from "./eventType";
import { TIMELINE_TIME_FORMAT } from "./TimelineSwimlane";

export const EVENT_LIST_PAGE_SIZE = 50;

interface TimelineEventListProps {
  events: TimelineEvent[];
  selectedEventId?: string;
  onSelect: (event: TimelineEvent) => void;
  /** Events that arrived with the last Live refresh (highlighted). */
  newEventIds?: ReadonlySet<string>;
  /**
   * Back to page 1 when this changes (participant, window, filters). Without
   * it, any change of `events` resets the page.
   */
  resetKey?: string;
}

const EMPTY_IDS: ReadonlySet<string> = new Set();

/**
 * Tabular alternative to the swimlane: readable without colour, usable with
 * a keyboard, and the place to scan dense windows where markers stack up.
 */
const TimelineEventList: React.FC<TimelineEventListProps> = ({
  events,
  selectedEventId,
  onSelect,
  newEventIds = EMPTY_IDS,
  resetKey,
}) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string) => string;
  const [page, setPage] = useState(0);

  // A Live refresh changes `events` but must not throw the user off their page.
  useEffect(() => {
    setPage(0);
  }, [resetKey ?? events]);

  const pageEvents = events.slice(
    page * EVENT_LIST_PAGE_SIZE,
    (page + 1) * EVENT_LIST_PAGE_SIZE,
  );

  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
        {t("timeline.listTitle")}
      </Typography>
      <TableContainer>
        <Table size="small" aria-label={t("timeline.listTitle")}>
          <TableHead>
            <TableRow>
              <TableCell>{t("timeline.columns.time")}</TableCell>
              <TableCell>{t("timeline.columns.lane")}</TableCell>
              <TableCell>{t("timeline.columns.event")}</TableCell>
              <TableCell>{t("timeline.columns.outcome")}</TableCell>
              <TableCell>{t("timeline.columns.reason")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageEvents.map((event) => (
              <TableRow
                key={event.id}
                hover
                selected={event.id === selectedEventId}
                tabIndex={0}
                data-new={newEventIds.has(event.id) || undefined}
                sx={{
                  cursor: "pointer",
                  ...(newEventIds.has(event.id) && {
                    animation: "timelineNewRow 6s ease-out forwards",
                    "@keyframes timelineNewRow": {
                      "0%": { backgroundColor: "rgba(98,83,162,0.18)" },
                      "100%": { backgroundColor: "transparent" },
                    },
                  }),
                }}
                onClick={() => onSelect(event)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(event);
                  }
                }}
                data-testid={`event-row-${event.id}`}
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatInBackofficeLondon(
                    event.occurredAt,
                    TIMELINE_TIME_FORMAT,
                  )}
                </TableCell>
                <TableCell>{tt(`lanes.${event.lane}`)}</TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {event.title}
                    <EventTypeTag event={event} />
                  </Box>
                </TableCell>
                <TableCell>
                  <OutcomeChip outcome={event.outcome} />
                </TableCell>
                <TableCell>{event.reason || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {events.length > EVENT_LIST_PAGE_SIZE && (
        <TablePagination
          component="div"
          count={events.length}
          page={page}
          rowsPerPage={EVENT_LIST_PAGE_SIZE}
          rowsPerPageOptions={[EVENT_LIST_PAGE_SIZE]}
          onPageChange={(_, newPage) => setPage(newPage)}
        />
      )}
    </>
  );
};

export default TimelineEventList;
